/**
 * React Query key factory — SALES keys (EPIC 11 SALE — Devis).
 *
 * Référence centrale dans `queryKeys.ts` (barrel). Doctrine d'invalidation
 * (_INTRO §6bis.2) : toute mutation de devis invalide aussi `clients.activities`
 * (onglet Activités de la fiche client, CLI-006). Le listing serveur (DataTable
 * mode="server") a ses PROPRES clés = la chaîne d'endpoint brute
 * (`['/api/sales/quotes']` + `['datatable-groups', '/api/sales/quotes']`) — il
 * faut les invalider EN PLUS de `quotes.all` (moule useCatalogArticles).
 */

const quotesAll = ['quotes'] as const
const quoteStatusesAll = ['quoteStatuses'] as const
const campaignsAll = ['campaigns'] as const
const quoteBoardsAll = ['quoteBoards'] as const
const salesStatsAll = ['salesStats'] as const
const pendingRequestsAll = ['pendingRequests'] as const
const waitlistsAll = ['waitlists'] as const
const planningRequestsAll = ['planningRequests'] as const
const renewalsAll = ['renewals'] as const
const commissionsAll = ['commissions'] as const

export const salesKeys = {
    quotes: {
        all: quotesAll,
        // Listing serveur — l'`entityId` est dans la clé (réponse is_inherited /
        // editable / deletable dépend du viewer courant, iso crmKeys.clients).
        list: (entityId: string, filters?: Record<string, unknown>) =>
            [...quotesAll, 'list', entityId, filters] as const,
        detail: (id: string, entityId: string) => [...quotesAll, 'detail', id, entityId] as const,
        // Préfixe pour invalidation cross-entity (matche toutes les variantes).
        detailAny: (id: string) => [...quotesAll, 'detail', id] as const,
        // SALE-002 — contexte de pré-remplissage du drawer de création.
        creationContext: (params?: Record<string, unknown>) =>
            [...quotesAll, 'creationContext', params] as const,
        // SALE-003/004 — lignes + synthèse financière (zone 2/3 de la page).
        lines: (id: string) => [...quotesAll, 'lines', id] as const,
        // SALE-005 — contexte de résolution de la synthèse financière (cascade, presets, B2G).
        financialContext: (id: string) => [...quotesAll, 'financialContext', id] as const,
        // Rappel client lecture seule (onglet Synthèse) — statut, lead, responsables, score.
        clientRecall: (id: string) => [...quotesAll, 'clientRecall', id] as const,
        // SALE-003 — picker « + Frais / + Article » (items ajoutables au devis).
        addableItems: (id: string, type: string, search: string) =>
            [...quotesAll, 'addableItems', id, type, search] as const,
        // SALE-007 — participants nominatifs (zone 4).
        learners: (id: string) => [...quotesAll, 'learners', id] as const,
        // SALE-019 — analyse des besoins rattachée + celles du client, vues
        // depuis le devis (une seule lecture pour les deux listes).
        needsAnalyses: (id: string) => [...quotesAll, 'needs-analyses', id] as const,
        // Frappe en cours : la clé porte les termes, jamais la collection —
        // sinon une invalidation de participants relancerait chaque recherche.
        learnerLookup: (id: string, terms: readonly string[]) =>
            [...quotesAll, 'learner-lookup', id, ...terms] as const,
        // SALE-007 §7.2quinquies — apprenants du client, à cocher. Clé distincte
        // du lookup : le filtre est le même texte, la question ne l'est pas.
        clientRoster: (id: string, search: string) =>
            [...quotesAll, 'client-roster', id, search] as const,
        // SALE-007 §7.3 — conséquences d'un retrait, pour la sélection en cours.
        removalPreview: (id: string, ids: readonly string[]) =>
            [...quotesAll, 'removal-preview', id, ...ids] as const,
        // SALE-007 §7.2quater — liens d'inscription envoyés au client.
        rosterLinks: (id: string) => [...quotesAll, 'roster-links', id] as const,
        // SALE-008 §7.4 — fil de vie (onglet Historique) : statuts, montants,
        // gestes internes, signaux client et signature en une seule lecture.
        // Remplace l'ancienne clé `events` (timeline des seuls statuts).
        activity: (id: string) => [...quotesAll, 'activity', id] as const,
        // SALE-006 — recherche de sessions à attacher (501 pré-SESS).
        sessionSearch: (params?: Record<string, unknown>) =>
            [...quotesAll, 'sessionSearch', params] as const,
        // SALE-011/012 — lien public + historique des envois (rail « Envois »).
        publicLink: (id: string) => [...quotesAll, 'publicLink', id] as const,
        dispatches: (id: string) => [...quotesAll, 'dispatches', id] as const,
        // SALE-010 — document T1 (état du bloc, aperçu live, historique des versions).
        document: (id: string) => [...quotesAll, 'document', id] as const,
        documentEditorData: (id: string, templateId: string | null, optionsKey: string) =>
            [...quotesAll, 'documentEditorData', id, templateId ?? '__default__', optionsKey] as const,
        documentVersions: (id: string) => [...quotesAll, 'documentVersions', id] as const,
        // SALE-012 — suivi d'ouverture du lien public (« Vu le »).
        tracking: (id: string) => [...quotesAll, 'tracking', id] as const,
        // SALE-012 — page PUBLIQUE `/quote/:token` (no-auth, clé par jeton :
        // le lecteur externe n'a ni entité courante ni session).
        publicView: (token: string) => [...quotesAll, 'publicView', token] as const,
        // SALE-013 — panneau Transformation (stock de groupes alloués).
        allocations: (id: string) => [...quotesAll, 'allocations', id] as const,
    },
    // SALE-008 — référentiel de statuts + raisons de perte (par entité).
    quoteStatuses: {
        all: quoteStatusesAll,
        list: (entityId: string) => [...quoteStatusesAll, 'list', entityId] as const,
        lossReasons: (entityId: string) => [...quoteStatusesAll, 'lossReasons', entityId] as const,
    },
    // SALE-022 — référentiel campagnes commerciales (picker inline, moule lead_types).
    campaigns: {
        all: campaignsAll,
        list: (entityId: string) => [...campaignsAll, 'list', entityId] as const,
    },
    // SALE-009 — tableaux kanban partagés (par entité) + colonnes agrégées.
    quoteBoards: {
        all: quoteBoardsAll,
        list: (entityId: string) => [...quoteBoardsAll, 'list', entityId] as const,
        detail: (boardId: string) => [...quoteBoardsAll, 'detail', boardId] as const,
        summary: (boardId: string, filters?: Record<string, unknown>) =>
            [...quoteBoardsAll, 'summary', boardId, filters] as const,
        column: (boardId: string, columnId: string, filters?: Record<string, unknown>) =>
            [...quoteBoardsAll, 'column', boardId, columnId, filters] as const,
        // Préfixes d'INVALIDATION (sans le segment filters : `summary(boardId)`
        // terminerait par `undefined`, qui ne prefix-matche pas un objet réel).
        summaryOf: (boardId: string) => [...quoteBoardsAll, 'summary', boardId] as const,
        columnsOf: (boardId: string) => [...quoteBoardsAll, 'column', boardId] as const,
        columnOf: (boardId: string, columnId: string) =>
            [...quoteBoardsAll, 'column', boardId, columnId] as const,
    },
    // SALE-018/020/021 — tableau de bord commercial.
    salesStats: {
        all: salesStatsAll,
        stats: (entityId: string, params?: Record<string, unknown>) =>
            [...salesStatsAll, 'stats', entityId, params] as const,
        targets: (entityId: string) => [...salesStatsAll, 'targets', entityId] as const,
    },
    // SALE-014 — demandes web (BOOST+).
    pendingRequests: {
        all: pendingRequestsAll,
        list: (entityId: string, filters?: Record<string, unknown>) =>
            [...pendingRequestsAll, 'list', entityId, filters] as const,
        detail: (id: string) => [...pendingRequestsAll, 'detail', id] as const,
        newCount: (entityId: string) => [...pendingRequestsAll, 'newCount', entityId] as const,
    },
    // SALE-015 — listes d'attente (BOOST+), agrégées par formation.
    waitlists: {
        all: waitlistsAll,
        list: (entityId: string, filters?: Record<string, unknown>) =>
            [...waitlistsAll, 'list', entityId, filters] as const,
        byProduct: (entityId: string, productId: string) =>
            [...waitlistsAll, 'byProduct', entityId, productId] as const,
    },
    // SALE-016 — demandes de planification (BUSINESS).
    planningRequests: {
        all: planningRequestsAll,
        list: (entityId: string, filters?: Record<string, unknown>) =>
            [...planningRequestsAll, 'list', entityId, filters] as const,
        detail: (id: string) => [...planningRequestsAll, 'detail', id] as const,
        counts: (entityId: string) => [...planningRequestsAll, 'counts', entityId] as const,
        byQuote: (quoteId: string) => [...planningRequestsAll, 'byQuote', quoteId] as const,
    },
    // SALE-017 — renouvellements (BUSINESS ; teaser à cette vague).
    renewals: {
        all: renewalsAll,
        list: (entityId: string, filters?: Record<string, unknown>) =>
            [...renewalsAll, 'list', entityId, filters] as const,
    },
    // SALE-026 — commissionnement (BUSINESS). Une clôture qui ne rafraîchit
    // rien est une clôture qui MENT : sans clé déclarée ni invalidation
    // prescrite, on clôture une période et le bandeau du bulletin affiche
    // encore « Ouverte » jusqu'au prochain montage.
    commissions: {
        all: commissionsAll,
        // L'entityId est DANS la clé : le calcul se fait par (personne, entité, période).
        rules: (entityId: string) => [...commissionsAll, 'rules', entityId] as const,
        periods: (entityId: string) => [...commissionsAll, 'periods', entityId] as const,
        statement: (periodId: string, userId?: string) =>
            [...commissionsAll, 'statement', periodId, userId ?? 'me'] as const,
        alerts: (entityId: string) => [...commissionsAll, 'alerts', entityId] as const,
        // Simulation par devis : clé DU MODULE (pas `quotes.*`), pour qu'une
        // révision de règle l'invalide en bloc via `commissions.all` sans
        // toucher au cache des devis.
        /**
         * ⚠️ **La RÉVISION du devis fait partie de la clé, et ce n'est pas une
         * optimisation.** Ce montant se recalcule à partir des lignes, des
         * remises et de la grille : dès qu'une quantité, un tarif ou un frais
         * change, la valeur en cache est fausse. Sans la révision dans la clé,
         * le commercial ajuste un prix et voit sa commission figée — donc il ne
         * peut pas « jouer sur les tarifs pour voir le montant évoluer » (PO,
         * 2026-08-13). Avec elle, chaque écriture sur le devis produit une
         * nouvelle clé, donc un refetch, sans avoir à invalider depuis chacune
         * des quinze mutations qui touchent un devis — et sans risquer d'en
         * oublier une la prochaine fois qu'on en ajoute.
         */
        quotePreview: (quoteId: string, revision?: string) =>
            [...commissionsAll, 'quote-preview', quoteId, revision ?? ''] as const,
        splits: (quoteId: string) => [...commissionsAll, 'splits', quoteId] as const,
        /**
         * §13.35 — la progression sur les objectifs commerciaux.
         *
         * ⚠️ Clé du MODULE commissions bien que la donnée vienne de
         * `sales_targets` : elle est lue sur les écrans de commission, et ce
         * sont eux qui doivent la rafraîchir. La ranger sous `salesStats`
         * l'aurait rendue invisible aux invalidations de ce module.
         */
        objectives: (entityId: string) => [...commissionsAll, 'objectives', entityId] as const,
        teamObjectives: (entityId: string) =>
            [...commissionsAll, 'objectives', 'team', entityId] as const,
    },
}
